import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import Head from 'next/head'
import InfiniteScroll from 'react-infinite-scroll-component'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'

import { useAPIGetInfiniteLearningGroups } from 'api/learningGroups/useAPILearningGroups'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import Header from 'app/LearningGroupPage/components/Header'
import LearningGroupLoading from './LearningGroupLoading'
import LearningGroupListCard from './components/LearningGroupListCard'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import { OfflineNotice } from 'components/OfflineNotice'
import { useOnlineStatus } from 'utils/hooks/useOnlineStatus'

const APP_LOGIN_KEY = 'app-login'

const ClassList = () => {
  const isOnline = useOnlineStatus()
  const [userData] = useLocalStorage(APP_LOGIN_KEY, false)
  const [currentOffset, setCurrentOffset] = useState(0)
  const [learningGroups, setLearningGroups] = useState([])
  const {
    data: learningGroupsInfiniteResponse,
    fetchNextPage,
    hasNextPage,
    status,
  } = useAPIGetInfiniteLearningGroups({ teacher: userData.email })

  useEffect(() => {
    setLearningGroups(
      learningGroupsInfiniteResponse?.pages.flatMap((page) => [...page.data]) ??
        []
    )
  }, [learningGroupsInfiniteResponse])

  const getNextPage = async () => {
    await fetchNextPage({
      pageParam: { limit: 20, offset: 20 * (currentOffset + 1) },
    })
    await setCurrentOffset((old) => {
      return old + 1
    })
  }

  return (
    <>
      <Header />
      <div
        className={clsx(
          'bg-gray-5 pt-4 px-4 min-h-full pb-16 mt-14',
          learningGroups.length === 0 && 'h-full'
        )}
      >
        <Head>
          <title>Kelas saya</title>
        </Head>
        {isOnline ? (
          <>
            {status === 'loading' && (
              <>
                {Array(6)
                  .fill(0)
                  .map((_, index) => (
                    <LearningGroupLoading key={index} />
                  ))}
              </>
            )}

            {status === 'error' && (
              <EmptyState
                withHeader={false}
                title="Gagal Memuat Kelas"
                description="Maaf, ada masalah pada sistem kami. Mohon coba lagi."
              >
                <EmptyStateAction
                  onClick={() => window.location.reload()}
                  color="black"
                >
                  Coba Lagi
                </EmptyStateAction>
              </EmptyState>
            )}

            {status === 'success' && learningGroups?.length <= 0 && (
              <div className="h-full flex justify-center items-center">
                <EmptyState
                  withHeader={false}
                  title="Belum Ada Kelas"
                  description="Membuat kelas akan memudahkan Anda membagikan dan melihat hasil asesmen murid."
                >
                  <EmptyStateAction href="/learning-group/new" color="black">
                    Buat Kelas
                  </EmptyStateAction>
                  <EmptyStateAction
                    href="https://pusatinformasi.guru.kemdikbud.go.id/hc/en-us/sections/7191054813593-Kelas"
                    fullWidth={false}
                    color="black"
                    className="mx-auto mt-4"
                  >
                    Info Lengkap
                  </EmptyStateAction>
                </EmptyState>
              </div>
            )}

            {status === 'success' && learningGroups.length > 0 && (
              <>
                <ul className="pb-4">
                  <InfiniteScroll
                    dataLength={20 * (currentOffset + 1)}
                    next={getNextPage}
                    hasMore={hasNextPage}
                    loader={
                      <div className="p-4">
                        <Shimmer
                          height={48}
                          className="my-4"
                          style={{ width: '100%' }}
                        />
                      </div>
                    }
                  >
                    {learningGroups.map((learningGroup) => (
                      <li key={learningGroup.id}>
                        <LearningGroupListCard learningGroup={learningGroup} />
                      </li>
                    ))}
                  </InfiniteScroll>
                </ul>
              </>
            )}

            {(status === 'loading' ||
              (status === 'success' && learningGroups.length > 0)) && (
              <Link href="/learning-group/new" passHref>
                <Button
                  color="blue"
                  size="lg"
                  className="fixed transform left-1/2 -translate-x-1/2 bottom-4"
                  style={{ borderRadius: '1.5rem' }}
                >
                  <Icon className="!text-white">add</Icon> Tambah Kelas
                </Button>
              </Link>
            )}
          </>
        ) : (
          <OfflineNotice className="h-full" />
        )}
      </div>
    </>
  )
}

export default ClassList
